import React from "react";
import logo from "../../images/logo2.svg";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../Context/AppContext";

const Footer = () => {
  const { contextCategories } = useApp();
  const navigate = useNavigate();

  const handleCategoryClick = (name, id) => {
    navigate(`/shop-by-category/${name.toLowerCase().replace(/\s+/g, "-")}`, {
      state: { category_id: id, category_name: name },
    });
  };

  const midIndex = Math.ceil(contextCategories.length / 2);
  const firstColumn = contextCategories.slice(0, midIndex);
  const secondColumn = contextCategories.slice(midIndex);

  /* ─── reusable sub-components ─── */

  const ColHeading = ({ children, to }) =>
    to ? (
      <Link to={to} className="block mb-3 group">
        <p className="font-poppins font-semibold text-[14px] text-white tracking-wide group-hover:text-[#5fbdb3] transition-colors">
          {children}
        </p>
        <span className="block w-7 h-[2px] bg-[#ff601e] rounded-full mt-1.5" />
      </Link>
    ) : (
      <div className="mb-3">
        <p className="font-poppins font-semibold text-[14px] text-white tracking-wide">
          {children}
        </p>
        <span className="block w-7 h-[2px] bg-[#ff601e] rounded-full mt-1.5" />
      </div>
    );

  const FLink = ({ to, onClick, children }) => (
    <Link
      to={to || "#"}
      onClick={onClick}
      className="font-poppins text-[12.5px] text-white/55 hover:text-white transition-colors duration-150 flex items-center gap-2 group py-[3px]"
    >
      <span className="w-1 h-1 rounded-full bg-[#5fbdb3] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      {children}
    </Link>
  );

  const ELink = ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-poppins text-[12.5px] text-white/55 hover:text-white transition-colors duration-150 flex items-center gap-2 group py-[3px]"
    >
      <span className="w-1 h-1 rounded-full bg-[#5fbdb3] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
      {children}
    </a>
  );

  return (
    <footer className="bg-[#162c2a] w-full font-poppins">
      {/* ── Top gradient accent ── */}
      <div className="w-full h-[3px] bg-gradient-to-r from-[#ff601e] via-[#5fbdb3] to-[#ff601e]" />

      {/* ══════════════════════════════════════════
          MAIN GRID
      ══════════════════════════════════════════ */}
      <div className="px-5 sm:px-8 lg:px-14 pt-10 pb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-8 gap-y-8 items-start">
          {/* ── COL 1 : Brand + Social (all screens) ── */}
          <div className="col-span-2 md:col-span-1 flex flex-col gap-4">
            {/* Logo */}
            <Link to="/home">
              <img
                src={logo}
                alt="MediSYS"
                className="h-14 w-auto object-contain"
              />
            </Link>

            {/* Tagline */}
            <p className="text-[12px] text-white/45 leading-relaxed max-w-[180px]">
              Your trusted pharmacy partner — delivering health to your door.
            </p>

            {/* Social icons — icon squares */}

            {/* Trust badge */}
          </div>

          {/* ── COL 2 : Quick Links ── */}
          <div className="flex flex-col col-span-1">
            <ColHeading>Quick Links</ColHeading>
            <FLink to="/about-us">About Us</FLink>
            <FLink to="/faq">FAQ</FLink>
            <FLink to="/terms-and-conditions">Terms & Conditions</FLink>
            <FLink to="/privacy-policy">Privacy Policy</FLink>
            <FLink to="/cancellation-policy">Cancellation Policy</FLink>
          </div>

          {/* ── COL 3 : Branch Locations ── */}
          <div className="flex flex-col col-span-1">
            <ColHeading to="/medical-stores">Branch Locations</ColHeading>
            <ELink href="https://g.co/kgs/4ETNhU7">Panjagutta, 500082</ELink>
            <ELink href="https://g.co/kgs/pwbp4Tj">Manikonda, 500104</ELink>
            <ELink href="https://g.co/kgs/bDwZkJW">Bandlaguda, 500091</ELink>
            <ELink href="https://g.co/kgs/uRqULhR">Chikkadapally, 500044</ELink>
            <ELink href="https://g.co/kgs/gPxULf4">Inderbagh, 500001</ELink>
            <Link
              to="/medical-stores"
              className="mt-2 ml-3 inline-flex items-center gap-1 text-[12px] font-semibold text-[#5fbdb3] hover:text-white transition-colors"
            >
              View All
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 12L10 8L6 4"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>

          {/* ── COL 4 : Categories first half ── */}
          <div className="flex flex-col col-span-1">
            <ColHeading>Categories</ColHeading>
            {firstColumn.map((item) => (
              <FLink
                key={item.category_id}
                onClick={(e) => {
                  e.preventDefault();
                  handleCategoryClick(item.category_name, item.category_id);
                }}
              >
                {item.category_name}
              </FLink>
            ))}
          </div>

          {/* ── COL 5 : Categories second half ── */}
          <div className="flex flex-col col-span-1">
            {/* Invisible heading spacer to align with col 4 */}
            <div className="mb-3 opacity-0 pointer-events-none select-none">
              <p className="text-[14px]">&nbsp;</p>
              <span className="block w-7 h-[2px] mt-1.5" />
            </div>
            {secondColumn.map((item) => (
              <FLink
                key={item.category_id}
                onClick={(e) => {
                  e.preventDefault();
                  handleCategoryClick(item.category_name, item.category_id);
                }}
              >
                {item.category_name}
              </FLink>
            ))}
          </div>

          {/* ── COL 6 : Contact Us ── */}
          <div className="flex flex-col col-span-2 md:col-span-1 gap-4">
            <ColHeading>Get In Touch</ColHeading>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/p/Medisys-Pharmacy-61553928654627/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white/[0.08] border border-white/[0.12] flex items-center justify-center hover:bg-[#ff601e] hover:border-[#ff601e] transition-all duration-200 hover:-translate-y-0.5"
              >
                <svg width="13" height="13" fill="white" viewBox="0 0 24 24">
                  <path d="M22.675 0H1.325C.593 0 0 .593 0 1.326v21.348C0 23.406.593 24 1.325 24h11.495v-9.294H9.689v-3.622h3.131V8.413c0-3.1 1.894-4.788 4.659-4.788 1.325 0 2.463.1 2.793.145v3.24l-1.918.001c-1.504 0-1.796.714-1.796 1.76v2.307h3.591l-.467 3.622h-3.124V24h6.116c.73 0 1.324-.593 1.324-1.326V1.326C24 .593 23.406 0 22.675 0z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/medisys_pharmacy?utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white/[0.08] border border-white/[0.12] flex items-center justify-center hover:bg-[#ff601e] hover:border-[#ff601e] transition-all duration-200 hover:-translate-y-0.5"
              >
                <svg width="13" height="13" fill="white" viewBox="0 0 24 24">
                  <path d="M7.75 2h8.5C19.554 2 22 4.446 22 7.75v8.5c0 3.304-2.446 5.75-5.75 5.75h-8.5C4.446 22 2 19.554 2 16.25v-8.5C2 4.446 4.446 2 7.75 2zm8.5 2h-8.5A3.75 3.75 0 0 0 4 7.75v8.5A3.75 3.75 0 0 0 7.75 20h8.5A3.75 3.75 0 0 0 20 16.25v-8.5A3.75 3.75 0 0 0 16.25 4zm-4.25 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm5.5-.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                </svg>
              </a>
              <a
                href="https://twitter.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-white/[0.08] border border-white/[0.12] flex items-center justify-center hover:bg-[#ff601e] hover:border-[#ff601e] transition-all duration-200 hover:-translate-y-0.5"
              >
                <svg width="13" height="13" fill="white" viewBox="0 0 24 24">
                  <path d="M0.922 0L10.182 13.5 1.118 24h2.008L11.07 14.793 17.385 24h7.588L15.298 9.897 23.835 0h-2.002L14.41 8.6 8.509 0H0.922zM3.32 1.263h4.524L22.574 22.737h-4.525L3.32 1.263z" />
                </svg>
              </a>
            </div>
            {/* Contact Us button */}
            <Link to="/contact-us">
              <button className="flex items-center gap-2 bg-[#ff601e] hover:bg-[#e55519] text-white font-semibold text-[13px] px-5 h-10 rounded-xl transition-all duration-200 shadow-[0_3px_12px_rgba(255,96,30,0.3)] hover:shadow-[0_5px_18px_rgba(255,96,30,0.45)] hover:-translate-y-0.5 whitespace-nowrap w-full justify-center sm:w-auto sm:justify-start">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12 19.79 19.79 0 0 1 1.65 3.4 2 2 0 0 1 3.62 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Contact Us
              </button>
            </Link>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="w-full h-px bg-white/[0.07] mt-8 mb-5" />

        {/* ── Bottom bar ── */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Copyright */}
          <p className="text-[12.5px] text-[#bfd3d1] text-center sm:text-left">
            &copy; {new Date().getFullYear()} MediSYS Pharmacy. All rights
            reserved.
          </p>

          {/* Policy links */}
          <div className="flex items-center gap-1 flex-wrap justify-center">
            {[
              { label: "Terms", to: "/terms-and-conditions" },
              { label: "Privacy", to: "/privacy-policy" },
              { label: "Contact", to: "/contact-us" },
            ].map((item, i, arr) => (
              <React.Fragment key={item.to}>
                <Link
                  to={item.to}
                  className="text-[12.5px] text-[#bfd3d1] hover:text-white/70 transition-colors px-2 py-0.5"
                >
                  {item.label}
                </Link>
                {i < arr.length - 1 && (
                  <span className="text-white/15 text-[12.5px] select-none">
                    |
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Powered by Novagito AI */}
          <a
            href="https://novagito.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-white/[0.05] border border-white/[0.08] rounded-xl px-4 py-2 hover:bg-white/[0.1] hover:border-[#5fbdb3]/40 transition-all duration-200 group flex-shrink-0"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              className="flex-shrink-0"
            >
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="#5fbdb3" />
            </svg>
            <span className="text-[11px] text-[#bfd3d1] group-hover:text-white/60 transition-colors font-poppins whitespace-nowrap">
              Powered by{" "}
              <span className="font-semibold text-[#39e213] group-hover:text-[#5fbdb3] transition-colors">
                Novagito AI
              </span>
            </span>
          </a>
        </div>
      </div>

      {/* ── Disclaimer strip ── */}
      <div className="w-full bg-[#0a1715] border-t border-white/[0.05] px-5 sm:px-8 lg:px-14 py-4">
        <p className="text-[11px] text-white/80 leading-relaxed text-justify italic">
          <span className="font-semibold text-red-400/80 not-italic">
            Disclaimer:
          </span>{" "}
          MediSYS Pharmacy{" "}
          <span className="font-semibold text-white/45 not-italic">
            (GSTIN: 36ABTFM9976H1Z7)
          </span>{" "}
          is a licensed pharmacy. Prescription medicines are supplied only
          against a valid doctor's prescription, verified by our pharmacists
          before delivery. The product information and content on this site are
          provided for convenience and general awareness and should not replace
          medical advice. Please consult a qualified healthcare professional for
          diagnosis and treatment. Prices, stock availability, and offers are
          subject to change. Final confirmation is provided at the time of order
          processing. Orders may be modified or cancelled if the required
          prescription is not valid or if items are unavailable. By using this
          website, you acknowledge and accept our Terms &amp; Conditions,
          Privacy Policy, and Return Policy.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
